import * as _ from "lodash-es";
import { computed, unref, type MaybeRef } from "vue";
import { useFieldMeta, type FieldMeta } from "./field-meta";
import { useInternalFormApi, type GenericValues, type InternalFormApiId } from "./form-context";
import type { Path, PathValue } from "./types";
import { safeClone } from "./utils";

export function readDataFromPath<TValues extends GenericValues, TPath extends Path<TValues>>(
	values: TValues,
	path: TPath
): PathValue<TValues, TPath> {
	return _.get(values, path) as PathValue<TValues, TPath>;
}

export function setDataFromPath<TValues extends GenericValues, TPath extends Path<TValues>>(
	values: TValues,
	path: TPath,
	value: PathValue<TValues, TPath>
) {
	_.set(values, path, value);
}

export function removeDataFromPath(values: any, path: string) {
	_.unset(values, path);
}

export function getInitialValue<TValue>(meta: MaybeRef<FieldMeta<TValue>>): TValue | undefined {
	return safeClone(unref(meta).initialValue) as any;
}

export function useFieldData<TValues extends GenericValues, TPath extends Path<TValues>>(
	id: InternalFormApiId<TValues>,
	path: TPath
) {
	const meta = useFieldMeta(id, path);
	const {
		state: { values },
	} = useInternalFormApi(id);

	const data = computed({
		get: () => readDataFromPath(values, path),
		set: (val) => {
			setDataFromPath(values, path, val);
		},
	});

	if (data.value === undefined && meta.value.initialValue !== undefined) {
		data.value = getInitialValue(meta) as any;
	}

	return data;
}
